import { apiService } from './api';

export interface PriceChangeRequest {
  id: string;
  siteId: string;
  publisherId: string;
  currentPrice: number;
  requestedPrice: number;
  reason?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  requestedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  rejectionReason?: string;
  site?: {
    site_url: string;
    category: string;
    da?: number;
    dr?: number;
  };
  publisher?: {
    publisherName: string;
    email?: string;
  };
  reviewer?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface CreatePriceChangeRequestData {
  siteId: string;
  requestedPrice: number;
  reason?: string;
}

class PriceChangeRequestService {
  /**
   * Create a new price change request (Publisher only)
   */
  async createRequest(data: CreatePriceChangeRequestData): Promise<PriceChangeRequest> {
    const response = await apiService.post<{ success: boolean; data: PriceChangeRequest; message: string }>(
      '/price-change-requests',
      data
    );
    return response.data;
  }

  /**
   * Get all price change requests
   * Publishers see only their own, Super Admin sees all
   */
  async getRequests(status?: 'PENDING' | 'APPROVED' | 'REJECTED'): Promise<PriceChangeRequest[]> {
    const params = status ? { status } : {};
    const response = await apiService.get<{ success: boolean; data: PriceChangeRequest[]; total: number }>(
      '/price-change-requests',
      params
    );
    return response.data;
  }

  /**
   * Get count of pending requests (Super Admin only)
   */
  async getPendingCount(): Promise<number> {
    const response = await apiService.get<{ success: boolean; count: number }>(
      '/price-change-requests/pending/count'
    );
    return response.count;
  }

  /**
   * Approve a price change request (Super Admin only)
   */
  async approveRequest(id: string): Promise<PriceChangeRequest> {
    const response = await apiService.put<{ success: boolean; data: PriceChangeRequest; message: string }>(
      `/price-change-requests/${id}/approve`,
      {}
    );
    return response.data;
  }

  /**
   * Reject a price change request (Super Admin only)
   */
  async rejectRequest(id: string, rejectionReason?: string): Promise<PriceChangeRequest> {
    const response = await apiService.put<{ success: boolean; data: PriceChangeRequest; message: string }>(
      `/price-change-requests/${id}/reject`,
      { rejectionReason }
    );
    return response.data;
  }

  /**
   * Format status for display
   */
  getStatusColor(status: string): 'warning' | 'success' | 'error' {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'warning';
    }
  }

  /**
   * Format status text
   */
  getStatusText(status: string): string {
    switch (status) {
      case 'PENDING':
        return 'Pending Review';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return status;
    }
  }

  /**
   * Calculate price change percentage
   */
  calculatePriceChange(currentPrice: number, requestedPrice: number): {
    amount: number;
    percentage: number;
    isIncrease: boolean;
  } {
    const amount = requestedPrice - currentPrice;
    const percentage = (amount / currentPrice) * 100;
    return {
      amount,
      percentage,
      isIncrease: amount > 0,
    };
  }
}

export const priceChangeRequestService = new PriceChangeRequestService();
